export const Professionalism_Growth = {
    ethicsAndCulture: {
        title: "Pawsible Culture: Ethics, Inclusion & Energy",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/ethicsAndCultureTitl.png",
        body: `
          <section>
            <h3>💖 What We Stand For</h3>
            <p>
              Pawsible isn’t just a workplace — it’s a community of care. 
              <span class="highlight">We lead with love, hold space for growth, and protect the emotional safety of every teammate and pet parent.</span>
              Professionalism here isn’t about formality — it’s how we show up with purpose and presence.
            </p>
          </section>
      
          <section>
            <h4>🌈 Inclusion in Action</h4>
            <ul>
              <li>👂 <strong>Listen first, ask thoughtfully:</strong> Every voice matters — even quiet ones</li>
              <li>🧩 <strong>Use inclusive language:</strong> Avoid jokes or comments that alienate, stereotype, or exclude</li>
              <li>🛑 <strong>Interrupt bias gently but clearly:</strong> <span class="highlight-yellow">“Hey, that might come off the wrong way — let’s keep it kind.”</span></li>
              <li>📢 <strong>Speak up with care:</strong> <span class="highlight-blue">Silence can make someone feel unseen — advocacy shows support</span></li>
            </ul>
          </section>
      
          <section>
            <h4>🪴 Ethics Over Ego</h4>
            <ul>
              <li>📦 <strong>No pressure sales:</strong> <span class="highlight">We only recommend what supports a pet's well-being</span></li>
              <li>🤝 <strong>Give credit freely:</strong> Team wins come from shared effort — we celebrate that</li>
              <li>🧠 <strong>Stay open to growth:</strong> Feedback isn't failure — it's a form of respect</li>
              <li>🔄 <strong>Own mistakes:</strong> Say “I missed that — thanks for catching it” and course-correct</li>
            </ul>
            <p><span class="highlight-green">Ethics show up in how we speak, act, and even how we restock supplies for the next shift.</span></p>
          </section>
      
          <section>
            <h4>⚡ The Pawsible Vibe</h4>
            <p>
              Our culture is felt in the little things:
            </p>
            <ul>
              <li>💬 Greeting a new teammate like a friend</li>
              <li>🧼 Cleaning a crate you didn’t use — just because it helps</li>
              <li>📣 Shouting out a win on team chat or in huddle</li>
              <li>🪞 Checking your tone and presence — <span class="highlight-yellow">am I adding warmth to the room?</span></li>
            </ul>
            <p><span class="highlight-green">Culture isn’t the big stuff — it’s how we treat each other when no one’s watching.</span></p>
            <div class="embedded-visual">
            </div>
            </section>
            
            <section>
            <h4>🌟 Real-Life Scenario</h4>
            <p>
            <em>During a busy afternoon, Mia notices a teammate looking overwhelmed. Without being asked, she takes over their walk rotation and leaves a sweet message later: “You’re killing it today — just wanted you to know.” No big gesture — just culture in action.</em>
            </p>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/ethicsAndCultureRealLif.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">We don’t teach culture — we live it.</span> 
              Lead with empathy, act with integrity, and let your daily actions define what Pawsible feels like.
            </p>
          </section>
        `
      },
      upsellingWithConfidence: {
        title: "Upselling With Confidence",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/upsellingWithConfidenceTitle.png",
        body: `
          <section>
            <h3>🎯 Not a Pitch — A Service</h3>
            <p>
              At Pawsible, we don’t sell — we <span class="highlight">share meaningful options that support the pet’s wellbeing and the parent’s peace of mind</span>. Upselling is simply offering a helpful solution when the moment is right.
            </p>
          </section>
      
          <section>
            <h4>🧠 Know What We Offer</h4>
            <ul>
              <li>🎓 <strong>Training:</strong> Is the pet struggling with recall or leash manners?</li>
              <li>🛁 <strong>Grooming:</strong> Can we support their coat, comfort, or hygiene?</li>
              <li>📅 <strong>Memberships:</strong> Is the family already visiting often — would bundling save them money?</li>
              <li>🎉 <strong>Specials:</strong> Birthday pupcakes, seasonal photos, enrichment kits — thoughtful touches that delight parents and elate pets</li>
            </ul>
            <p><span class="highlight-green">Know our services well — and use that knowledge to serve, not sell.</span></p>
          </section>
      
          <section>
            <h4>💬 How to Offer It</h4>
            <ul>
              <li>👂 <strong>Listen first:</strong> “You’ve mentioned grooming’s tough at home — want us to help while they’re here?”</li>
              <li>🤝 <strong>Empathize:</strong> “We see that a lot — here's what we’ve found works well…”</li>
              <li>💡 <strong>Frame it as care, not convenience:</strong> “It helps dogs decompress faster after playgroup.”</li>
            </ul>
            <p><span class="highlight-blue">It’s not about pushing — it’s about making life easier, safer, or sweeter for them.</span></p>
          </section>
      
          <section>
            <h4>🚫 What Not to Do</h4>
            <ul>
              <li>❌ Don’t recommend blindly — <span class="highlight-red">know the pet, know the parent</span></li>
              <li>❌ Don’t use urgency gimmicks — trust builds loyalty, not FOMO</li>
              <li>❌ Don’t assume budget — offer, but never pressure</li>
            </ul>
          </section>
      
          <section>
            <h4>🌟 Real-Life Scenario</h4>
            <p>
              <em>Milo’s coat is matted after daycare. Jen gently tells the parent, “He played hard today — a brush-out before pickup can help avoid discomfort. We can do it now or flag it for next time!” The parent agrees with thanks — they feel cared for, not sold to.</em>
            </p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/upsellingWithConfidenceRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight-green">Upselling is care with confidence.</span> If it improves the experience for the pet or parent, offer it with heart — and always let them choose.
            </p>
          </section>
        `
      },      
      brandAmbassadorSkills: {
        title: "Hosting Events & Representing the Brand",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/brandAmbassadorSkillsTitle.png",
        body: `
          <section>
            <h3>🎈 You Are the Brand</h3>
            <p>
              Whether you're setting up a booth, leading a training, or greeting new parents — <span class="highlight">you are the face of Pawsible</span>. Events are our chance to build trust, joy, and lasting impressions in pet ownership.
            </p>
          </section>
      
          <section>
            <h4>📋 Prepare Like a Pro</h4>
            <ul>
              <li>📦 <strong>Double-check supplies:</strong> Leashes, signage, cleanup gear, event kits</li>
              <li>🎤 <strong>Know the plan:</strong> Who's leading what? What are our talking points?</li>
              <li>📲 <strong>Know the services:</strong> Be ready to speak confidently about memberships, training, and scheduling</li>
              <li>🎨 <strong>Polish the details:</strong> Neat signage, clean setup, Pawsible shirts worn with pride</li>
            </ul>
          </section>
      
          <section>
            <h4>👥 Energy That Draws People In</h4>
            <ul>
              <li>🧍‍♀️ <strong>Stand tall, stay approachable:</strong> Open body language, friendly eye contact</li>
              <li>🫶 <strong>Lead with heart, not a sales pitch:</strong> “Can I tell you what makes us different?” goes further than “We’re offering...”</li>
              <li>🎯 <strong>Make it personal:</strong> “What kind of pet do you have?” starts a relationship, not a transaction</li>
              <li>🧠 <strong>Know the tone:</strong> Warm, clear, never pushy — let our reputation speak through you</li>
            </ul>
            <p><span class="highlight-blue">The energy you bring *is* the brand experience.</span></p>
          </section>
      
          <section>
            <h4>🎁 Moments That Leave a Mark</h4>
            <ul>
              <li>📸 Offer photo ops or giveaways (with parent consent)</li>
              <li>🎉 Make each pup feel like a VIP — use names, gentle handling, and kind praise</li>
              <li>💌 Follow up if possible: “I loved meeting Biscuit today! Here’s our contact if you ever want a tour.”</li>
            </ul>
            <p><span class="highlight-green">Small, genuine gestures leave big impressions.</span></p>
          </section>
      
          <section>
            <h4>🌟 Real-Life Scenario</h4>
            <p>
              <em>Jordan hosts a pop-up table at an adoption event. Rather than pitching memberships, they smile and say, “We’re the soft landing spot after the adoption day. Here’s what we offer if you ever need a safe, loving place for your new family member.” Parents linger. Dogs get treats. Hearts open.</em>
            </p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/brandAmbassadorSkillsRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">You don’t just represent the brand — you *are* the brand.</span> Every word, every smile, every detail says, “This is what care looks like.” Show up like it matters — because it does.
            </p>
          </section>
        `
      },    
      foodHandlingAndFeeding: {
        title: "Safe Feeding & Food Handling Practices",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/foodHandlingAndFeedingTitle.png",
        body: `
          <section>
            <h3>🍽️ Safe Food = Safe Pets</h3>
            <p>
              Feeding isn’t just routine — <span class="highlight">it’s a trust-based, high-responsibility moment</span>. From allergies to bowl placement, every step matters. Clean, careful, and customized — that's the Pawsible way.
            </p>
          </section>
      
          <section>
            <h4>🧼 Food Handling Hygiene</h4>
            <ul>
              <li>🧴 <strong>Wash hands</strong> before and after handling food</li>
              <li>🧽 <strong>Sanitize bowls & surfaces</strong> after every use — never reuse unwashed dishes</li>
              <li>🧊 <strong>Refrigerate</strong> wet or raw food immediately if not in use</li>
              <li>🧃 <strong>Label and date</strong> all perishable food items brought by clients</li>
            </ul>
            <p><span class="highlight-green">Clean food prep = healthy pets and no surprises.</span></p>
          </section>
      
          <section>
            <h4>📋 Feeding Protocols by Pet</h4>
            <ul>
              <li>📝 <strong>Check the dashboard</strong> for every pet’s feeding notes before serving</li>
              <li>🐾 <strong>Confirm allergies:</strong> No shared treats or food swaps unless pre-approved</li>
              <li>⏰ <strong>Stick to schedule:</strong> Follow exact times when listed (especially for medical conditions)</li>
              <li>🥣 <strong>Correct location:</strong> Feed in calm, assigned areas — <span class="highlight-red">separate zones for nervous or food-aggressive pets</span></li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/foodHandlingAndFeedingFeeding.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-yellow">Every missed instruction is a potential safety issue — read before you serve.</span></p>
            </section>
      
          <section>
            <h4>🐶 Treats & Enrichment Feeding</h4>
            <ul>
              <li>🎁 <strong>Use enrichment intentionally:</strong> Stuffed Kongs, slow feeders, puzzle bowls as calming tools</li>
              <li>❓ <strong>Ask first:</strong> If a pet doesn’t have “okay to treat” listed, confirm with a lead</li>
              <li>🍬 <strong>No human snacks:</strong> Never give food outside our approved list or client-provided bags</li>
            </ul>
            <p><span class="highlight-blue">Enrichment should soothe, not excite. Be mindful of pacing and energy levels post-feeding.</span></p>
          </section>
      
          <section>
            <h4>🚩 Red Flags to Watch</h4>
            <ul>
              <li>🥺 Refusal to eat more than one meal in a row</li>
              <li>🚫 Vomiting, diarrhea, or sudden food guarding</li>
              <li>📉 Major changes in appetite, especially for boarding guests</li>
            </ul>
            <p><span class="highlight-red">Always report changes to a lead. Better safe than sorry.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Feeding is a moment of trust and care.</span> Keep it clean. Keep it calm. Keep it correct. That’s how we feed the Pawsible way.
            </p>
          </section>
        `
      },
      mentorshipProgram: {
        title: "Mentorship & Buddy Program",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/mentorshipProgramTitle.png",
        body: `
          <section>
            <h3>🫶 Onboard Like a Family, Not a Franchise</h3>
            <p>
              At Pawsible, we don’t just train new hires — <span class="highlight">we welcome them into our culture</span>. Our buddy system builds trust, accelerates growth, and models what calm, collaborative work looks like.
            </p>
          </section>
      
          <section>
            <h4>👯 Who's a Buddy?</h4>
            <ul>
              <li>🧠 <strong>Trained in our SOPs:</strong> Knows protocols, culture, and expectations inside and out</li>
              <li>🫶 <strong>Emotionally mature:</strong> Leads with kindness and clarity — never shaming or showing off</li>
              <li>🎯 <strong>Reliable and organized:</strong> Tracks progress, gives feedback, and supports questions with care</li>
            </ul>
            <p><span class="highlight-green">Being a buddy is an honor — it means you represent what Pawsible stands for.</span></p>
          </section>
      
          <section>
            <h4>📚 What Buddies Do</h4>
            <ul>
              <li>🧭 Give a Day 1 tour and explain why each operation matters</li>
              <li>📋 Walk through the staff dashboard — especially SOPs, schedules, and incident reports</li>
              <li>🧼 Model tasks: cleaning, feeding, handling, safety protocols</li>
              <li>🧩 Answer “dumb questions” without judgment — because there’s no such thing</li>
              <li>🌱 Check in weekly for the first month to offer tips, encouragement, and feedback</li>
            </ul>
            <p><span class="highlight-blue">Buddies don’t just explain — they demonstrate.</span> Calm energy. Confident hands. Consistent standards.</p>
          </section>
      
          <section>
            <h4>🌟 What New Hires Should Expect</h4>
            <ul>
              <li>👂 A non-judgmental space to learn and mess up</li>
              <li>📘 Clear answers and repeat explanations as needed</li>
              <li>🎉 Celebrations for small wins — first task learned, first enrichment delivered</li>
              <li>💬 Real talk — honest feedback that’s kind, direct, and solution-oriented</li>
            </ul>
            <p><span class="highlight-yellow">This isn’t sink or swim. It’s learn and lead — together.</span></p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/mentorshipProgramBuddies.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🚀 Growth Path for Buddies</h4>
            <ul>
              <li>🌱 Excellent attendance and strong performance</li>
              <li>📣 Must be assigned by a manager</li>
              <li>🏆 Earns credits toward promotion consideration and higher roles</li>
            </ul>
            <p><span class="highlight">Mentorship is a step into leadership — one calm moment at a time.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight-green">We don’t build culture through rules — we build it through people.</span> The buddy program helps us train with heart, guide with grace, and lift up the next wave of greatness at Pawsible.
            </p>
          </section>
        `
      },
      careerPathway: {
        title: "Career Growth & Promotion Criteria",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/careerPathwayTitle.png",
        body: `
          <section>
            <h3>🚀 Growth That’s Visible, Earned, and Celebrated</h3>
            <p>
              At Pawsible, <span class="highlight">career growth is built into the job — not an afterthought</span>. From Day 1, we make it clear: if you care deeply, show up consistently, and lead with heart, we’ll open doors.
            </p>
          </section>
      
          <section>
            <h4>🎯 What We Look For</h4>
            <ul>
              <li>📈 <strong>Performance:</strong> You meet or exceed expectations in daily tasks, safety, and communication</li>
              <li>🧠 <strong>Learning:</strong> You’ve completed required training modules and SOP certifications</li>
              <li>🌱 <strong>Mindset:</strong> You take feedback with grace, uplift others, and reflect the Pawsible vibe</li>
            </ul>
            <p><span class="highlight-blue">Promotions are earned with consistency — not just charisma.</span></p>
          </section>
      
          <section>
            <h4>🪜 Pathways Within Pawsible</h4>
            <ul>
              <li>🦴 <strong>Level 1 Staff:</strong> Shadow, assist, and complete basic certifications</li>
              <li>🐾 <strong>Level 2 Staff:</strong> Independently lead zones, support new hires, contribute to huddles</li>
              <li>💬 <strong>Client Experience Specialist:</strong> Handle front desk, bookings, parent communication, and facility energy</li>
              <li>🧷 <strong>Client Experience Manager:</strong> Oversee all front-desk ops, client relationships, communications, and events</li>
              <li>🧭 <strong>Team Lead:</strong> Manage shift execution, team dynamics, and operational flow</li>
              <li>🎓 <strong>Trainer / Groomer:</strong> Deliver client-facing services with expertise and warmth</li>
              <li>🏢 <strong>Manager:</strong> Lead location operations, mentor staff, and uphold Pawsible standards</li>
              <li>🌍 <strong>District Manager:</strong> Guide multiple locations, develop leaders, and drive brand-wide success</li>
            </ul>
            <p><span class="highlight-green">Whether your passion is pets or people — or both — Pawsible creates space for you to grow.</span></p>
          </section>

          <section>
            <h4>📋 How Promotion Works</h4>
            <ul>
              <li>🗓️ Check-ins happen quarterly with your manager or lead</li>
              <li>📘 You’ll review progress against training goals and team impact</li>
              <li>🏆 Promotion decisions are based on both <span class="highlight">technical readiness and emotional intelligence</span></li>
            </ul>
            <p><span class="highlight-yellow">We don’t just promote skill — we promote trust, culture, and care.</span></p>
          </section>
      
          <section>
            <h4>🌟 Real-Life Example</h4>
            <p>
              <em>Kolton started as a floater with zero pet industry experience. He asked questions, stayed late to help, and logged every incident with precision. By Month 4, he became a buddy. By Month 6, he led his first weekend shift solo. At his 9-month review, he was promoted to Team Lead.</em>
            </p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/careerPathwayRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Growth at Pawsible isn’t a mystery — it’s a map.</span> Learn. Lead. Support the team. And when you're ready — we notice.
            </p>
          </section>
        `
      },          
}
  